import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseSparklinkParams } from '@arthelper/shared/sparklink';
import { uuid } from '@arthelper/shared/uuid';

import { useAuth } from '~/context/auth';
import { useChatHook, type ChatOpening } from '~/hooks/useConversationChat';
import { DEFAULT_MODEL_ID } from '~/components/Chat/constants';
import type { ArtySendOptions } from '~/components/arty/card';
import { notify } from '~/components/arty/actions/ArtyActionsProvider';
import { useAnalytics } from '~/context/analytics';
import { autoSubmitKey, clearAutoSubmitted, toSparklinkParams } from '~/lib/arty-flyout/sparklink';

import { useLandingSubmit, type Landing } from './useLandingSubmit';
import { ArtyFlyout } from './ArtyFlyout';

export type ArtyFlyoutOpenInput = {
  /** A sparklink: a full URL (any host; only the query is read), a query string, or URLSearchParams. */
  sparklink?: string | URLSearchParams;
  /** Where the open came from, for analytics. */
  source?: 'notification' | 'notification_toast' | string;
};

export type ArtyFlyoutApi = {
  isOpen: boolean;
  isBusy: boolean;
  open: (input?: ArtyFlyoutOpenInput) => void;
  close: () => void;
  sendText: (text: string, options?: ArtySendOptions) => Promise<void> | void;
  /** The same turn from outside the flyout (an image's "..." menu): opens it on a fresh chat first. */
  sendTextInNewChat: (text: string, options?: ArtySendOptions) => void;
};

type ArtyFlyoutView = Pick<ArtyFlyoutApi, 'isOpen' | 'close' | 'sendText'> & {
  chat: any;
  cardTurn: number;
  isSignedIn: boolean;
  newChat: () => void;
  panel: 'chat' | 'history';
  showHistory: () => void;
  showChat: () => void;
  selectChat: (chatId: string) => void;
};

const ArtyFlyoutContext = createContext<ArtyFlyoutApi | null>(null);

// Split from the API so a page holding `useArtyFlyout()` does not re-render on every streamed token.
const ArtyFlyoutViewContext = createContext<ArtyFlyoutView | null>(null);

function openingOf(landing: Landing): ChatOpening {
  return { sparkId: landing.parsed.sparkId, context: landing.parsed.context };
}

function deviceClass(): 'mobile' | 'desktop' {
  return window.matchMedia('(max-width: 639px)').matches ? 'mobile' : 'desktop';
}

/** With chat off nothing can open, so the chat hook and its transport are never built. */
export function ArtyFlyoutProvider({ children }: { children: any }) {
  if (!__CHAT_ENABLED__) {
    return children;
  }
  return <EnabledArtyFlyoutProvider>{children}</EnabledArtyFlyoutProvider>;
}

function EnabledArtyFlyoutProvider({ children }: { children: any }) {
  const { user } = useAuth();
  const { amplitude } = useAnalytics();
  const { t: tCard } = useTranslation('arty-card');
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [landing, setLanding] = useState<Landing | null>(null);
  const [opening, setOpening] = useState<ChatOpening>({});
  const [cardTurn, setCardTurn] = useState(0);
  const [panel, setPanel] = useState<'chat' | 'history'>('chat');

  const chat = useChatHook({
    targetType: 'standalone',
    targetId: undefined,
    userId: user?.user_id || user?.id,
    model: DEFAULT_MODEL_ID,
    standalone: true,
    opening,
  });
  const chatRef = useRef(chat);
  chatRef.current = chat;

  const onResolved = useCallback((image: any) => {
    if (image) {
      setOpening((current) => ({ ...current, imageId: image.imageId }));
    }
  }, []);

  // Only the landing that finished is cleared; a newer one set meanwhile is left alone.
  const onDone = useCallback((finished: Landing) => {
    setLanding((current) => (current === finished ? null : current));
  }, []);

  useLandingSubmit({ landing, chat, onResolved, onDone });

  // The same two events town-square fires when a sparklink lands and opens its panel, so the
  // first-upload funnel keeps its landing when the click stays in client. Ids and flags only.
  const track = useCallback(
    (parsed: any, source: string) => {
      amplitude.track('sn_arty_sparklink_landing', {
        sparklink_landing_id: uuid(),
        ...(parsed.sparkId ? { spark_id: parsed.sparkId } : {}),
        chat_destination: parsed.destination ?? 'community',
        auth_state: 'signed_in',
        device_class: deviceClass(),
        route: location.pathname,
        has_message_param: !!parsed.message,
        has_images_param: !!parsed.images?.length,
        has_context_param: !!parsed.context,
      });
      amplitude.track('sn_arty_panel_opened', {
        panel_open_source: source,
        auth_state: 'signed_in',
        device_class: deviceClass(),
      });
    },
    [amplitude, location.pathname]
  );

  const open = useCallback(
    (input?: ArtyFlyoutOpenInput) => {
      if (!input?.sparklink) {
        setIsOpen(true);
        return;
      }

      const params = toSparklinkParams(input.sparklink);
      const parsed = parseSparklinkParams(params);
      if (parsed.destination === 'assistant') {
        navigate(`/chat?${params.toString()}`);
        return;
      }

      // A click is deliberate, so the same link sends again into a fresh chat; the one-shot
      // key only guards against a reload replaying it, as in town-square's programmatic mode.
      const key = autoSubmitKey(parsed, location.pathname);
      clearAutoSubmitted(key);
      const chatId = chatRef.current.startChat();
      const next = { parsed, key, chatId };
      setOpening(openingOf(next));
      setLanding(next);
      setPanel('chat');
      setIsOpen(true);
      track(parsed, input.source ?? 'programmatic');
    },
    [navigate, location.pathname, track]
  );

  // Closing drops a landing still resolving its piece, so nothing auto-sends into a sheet
  // nobody is looking at and the one-shot key stays unclaimed for the next open.
  const close = useCallback(() => {
    setLanding(null);
    setIsOpen(false);
  }, []);

  // A blank chat starts with no spark, piece or context; only a landing brings those.
  const newChat = useCallback(() => {
    setLanding(null);
    setOpening({});
    setPanel('chat');
    chatRef.current.startChat();
  }, []);

  // A past chat resumes with nothing from the last landing riding its next turn, and nothing
  // typed for the chat being left sits in its composer.
  const selectChat = useCallback((chatId: string) => {
    setLanding(null);
    setOpening({});
    setPanel('chat');
    chatRef.current.setInput('');
    chatRef.current.navigateToChat(chatId);
  }, []);

  const showHistory = useCallback(() => {
    setPanel('history');
  }, []);

  const showChat = useCallback(() => {
    setPanel('chat');
  }, []);

  // The piece's url and title stay on the card: the API resolves the id itself, so only a piece
  // the member may see ever reaches the prompt. The card has already closed its sheet, so a turn
  // that does not go out says so instead of looking sent.
  const sendText = useCallback(
    async (text: string, options?: ArtySendOptions) => {
      try {
        const sent = await chatRef.current.sendText(text, { sparkId: options?.sparkId, imageId: options?.imageId });
        if (!sent) {
          notify(tCard('toast.busy'));
          return;
        }
        setCardTurn((count) => count + 1);
      } catch (error) {
        console.error('Failed to send Arty card turn:', error);
        notify(tCard('toast.action-failed'), 'error');
      }
    },
    [tCard]
  );

  // The turn waits for the render that swaps in the fresh chat, as a sparklink landing does:
  // until `draftChatId` matches, `chatRef` still holds the chat being left.
  const [pendingTurn, setPendingTurn] = useState<{ chatId: string; text: string; options?: ArtySendOptions } | null>(
    null
  );

  const sendTextInNewChat = useCallback(
    (text: string, options?: ArtySendOptions) => {
      setLanding(null);
      setOpening({});
      setPanel('chat');
      const chatId = chatRef.current.startChat();
      setPendingTurn({ chatId, text, options });
      setIsOpen(true);
      amplitude.track('sn_arty_panel_opened', {
        panel_open_source: 'image_menu',
        auth_state: 'signed_in',
        device_class: deviceClass(),
      });
    },
    [amplitude]
  );

  useEffect(() => {
    if (!pendingTurn || chat.draftChatId !== pendingTurn.chatId) {
      return;
    }
    setPendingTurn(null);
    void sendText(pendingTurn.text, pendingTurn.options);
  }, [chat.draftChatId, pendingTurn, sendText]);

  const api = useMemo(
    () => ({ isOpen, isBusy: chat.isLoading || chat.awaitingWindow, open, close, sendText, sendTextInNewChat }),
    [isOpen, chat.isLoading, chat.awaitingWindow, open, close, sendText, sendTextInNewChat]
  );
  const view = {
    chat,
    cardTurn,
    isSignedIn: !!user,
    isOpen,
    close,
    sendText,
    newChat,
    panel,
    showHistory,
    showChat,
    selectChat,
  };

  return (
    <ArtyFlyoutContext.Provider value={api}>
      {children}
      <ArtyFlyoutViewContext.Provider value={view}>
        <ArtyFlyout />
      </ArtyFlyoutViewContext.Provider>
    </ArtyFlyoutContext.Provider>
  );
}

export function useArtyFlyoutView(): ArtyFlyoutView {
  const context = useContext(ArtyFlyoutViewContext);
  if (!context) {
    throw new Error('useArtyFlyoutView must be used within ArtyFlyoutProvider');
  }

  return context;
}

/**
 * Null outside the provider and in a build with chat off, so callers fall back to
 * the link they would have followed before the flyout existed.
 */
export function useArtyFlyoutOptional(): ArtyFlyoutApi | null {
  return useContext(ArtyFlyoutContext);
}

export function useArtyFlyout(): ArtyFlyoutApi {
  const context = useContext(ArtyFlyoutContext);
  if (!context) {
    throw new Error('useArtyFlyout must be used within ArtyFlyoutProvider');
  }

  return context;
}
